import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Compass, Heart, ArrowRight, CheckCircle2, Clock, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";

const ICON = { big5: Brain, riasec: Compass, work_values: Heart };
const ACCENT = { big5: "bg-primary/10 text-primary", riasec: "bg-[hsl(var(--warning))/0.15] text-[hsl(var(--warning))]", work_values: "bg-[hsl(var(--success))/0.15] text-[hsl(var(--success))]" };

export default function Assessments() {
  const nav = useNavigate();
  const [list, setList] = useState(null);

  useEffect(() => {
    api.get("/assessments").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);

  return (
    <div data-testid="assessments-page">
      <PageHeader
        eyebrow="Module 09"
        title="Personality & Aptitude"
        subtitle="Take a validated 5-minute assessment. AI translates the results into career families, strengths, and specific next actions personalized to your profile."
      />

      {!list ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CardSkeleton /><CardSkeleton /><CardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {list.map((a, i) => {
            const Icon = ICON[a.type] || Brain;
            return (
              <motion.article
                key={a.type}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-card border border-border rounded-xl p-6 flex flex-col card-hover"
                data-testid={`assessment-${a.type}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ACCENT[a.type] || "bg-primary/10 text-primary"}`}>
                    <Icon size={18} strokeWidth={1.75} />
                  </div>
                  {a.completed && (
                    <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--success))]" data-testid={`assessment-${a.type}-completed`}>
                      <CheckCircle2 size={12} /> Completed
                    </div>
                  )}
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight text-foreground mb-2">{a.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 min-h-[64px]">{a.description}</p>
                <div className="flex items-center gap-4 mb-5 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {a.duration_min} min</span>
                  <span className="inline-flex items-center gap-1"><HelpCircle size={11} /> {a.questions} Q</span>
                </div>
                <div className="mt-auto flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => nav(`/app/assessments/${a.type}`)}
                    data-testid={`assessment-${a.type}-start`}
                    className="gap-2 flex-1"
                  >
                    {a.completed ? "Retake" : "Start"} <ArrowRight size={14} />
                  </Button>
                  {a.completed && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => nav(`/app/assessments/${a.type}/result`)}
                      data-testid={`assessment-${a.type}-view-result`}
                    >
                      View result
                    </Button>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      )}
    </div>
  );
}
